import { useMemo } from 'react';
import type { SeriesMetric, ServiceSeries } from '@/api/observability';
import { getServiceSeries } from '@/api/observability';
import { useApiQuery } from './useApiQuery';

const UNITS: Record<SeriesMetric, string> = {
  latency: 'ms',
  errorRate: '%',
  rps: 'req/min',
};

/** Generates a plausible local-dev sparkline so the UI renders without a backend. */
const buildMockSeries = (metric: SeriesMetric): ServiceSeries => {
  const base = metric === 'latency' ? 40 : metric === 'rps' ? 25 : 0.5;
  const points = Array.from({ length: 48 }, (_, i) => ({
    t: Math.floor(Date.now() / 1000) - (48 - i) * 1800,
    v: Math.max(0, base + Math.sin(i / 4) * base * 0.3 + (Math.random() - 0.5) * base * 0.2),
  }));
  return { metric, window: '24h', unit: UNITS[metric], points };
};

interface UseServiceSeriesResult {
  series: ServiceSeries | undefined;
  isLoading: boolean;
}

/**
 * Lazily fetches a single metric's 24h trend for one service. The backend
 * serves a short-TTL cached series, so each card fetching its own trend is a
 * cheap read.
 */
export const useServiceSeries = (
  serviceId: string,
  metric: SeriesMetric,
  enabled = true,
  window = '24h',
): UseServiceSeriesResult => {
  const mock = useMemo(
    () => (import.meta.env.DEV ? buildMockSeries(metric) : undefined),
    [metric],
  );

  const query = useApiQuery<ServiceSeries>(
    {
      queryFn: (signal) => getServiceSeries(serviceId, metric, window, signal),
      mockData: mock,
      enabled,
    },
    [serviceId, metric, window, enabled],
  );

  return { series: query.data, isLoading: query.isLoading };
};
